/**
 * Frontend domain types.
 *
 * These types are derived from API types and used by components.
 * They represent the data shapes after transformation for rendering.
 */

/** Toast notification variant */
export type ToastVariant = 'info' | 'success' | 'error' | 'warning'

/** Route match result */
export interface RouteMatch {
  handler: (params: Record<string, string>) => void
  params: Record<string, string>
}

/** A node in the unrolled execution graph */
export interface ExecutionNode {
  stage: string
  status: 'done' | 'running' | 'failed' | 'pending'
  duration: string | null
  turns: number | null
  tokens?: { in: number; out: number } | null
  iteration: number | null
  onClick?: string
}

/** A parallel fan-out block (review stage) */
export interface FanOutBlock {
  nodes: readonly ExecutionNode[]
  allApproved: boolean
}

/** A loop segment: Impl -> Review fan-out -> Consolidate */
export interface LoopSegment {
  loopNumber: number
  status: 'rejected' | 'approved'
  implementer: ExecutionNode
  review: FanOutBlock
  consolidate?: ExecutionNode
}

/** The full unrolled execution graph */
export interface ExecutionGraph {
  productManager: ExecutionNode
  loops: readonly LoopSegment[]
  reflect?: ExecutionNode
  introspect?: ExecutionNode
  integrate?: ExecutionNode
  commitHash?: string
}

/** Gate review result for a single reviewer within a loop */
export interface GateReviewResult {
  reviewer: string
  approved: boolean
  summary: string | null
  detail: string | null
  response: string | null
  files: readonly string[]
}

/** A complete loop review (for Overview tab) */
export interface LoopReview {
  loopNumber: number
  status: 'rejected' | 'approved'
  gates: readonly GateReviewResult[]
  consolidateText?: string
  consolidateResponse?: string
  consolidateFiles?: readonly string[]
}

/** Task list item */
export interface TaskItem {
  text: string
  status: 'done' | 'active' | 'pending'
  duration: string | null
}

/** Task list with progress */
export interface TaskListData {
  title: string
  progress: string
  items: readonly TaskItem[]
}

/** Display-ready stage name helpers */
const STAGE_SHORT_LABELS: Readonly<Record<string, string>> = {
  'product-manager': 'PM',
  'bdd-reviewer': 'BDD',
  semlint: 'Lint',
  implementer: 'Impl',
  critic: 'Critic',
  gate: 'Gate',
  consolidate: 'Consol',
  reflect: 'Reflect',
  introspect: 'Introspect',
  integrate: 'Integrate',
}

/** Get a short display label for a dynamic stage name */
export function formatStageName(stage: string): string {
  return STAGE_SHORT_LABELS[stage] ?? stage
}

/** Format seconds into human-readable duration */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) {
    return null
  }
  if (seconds < 0) {
    return null
  }
  if (seconds < 1) {
    return '0s'
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  }
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

/** Format token count with K/M suffixes */
export function formatTokens(count: number | null | undefined): string {
  if (count === null || count === undefined) {
    return '\u2014'
  }
  if (count < 1000) {
    return String(count)
  }
  if (count < 1_000_000) {
    return `${(count / 1000).toFixed(1)}K`
  }
  return `${(count / 1_000_000).toFixed(1)}M`
}

/** CSS class for a node status */
export function statusClass(status: 'done' | 'running' | 'failed' | 'pending'): string {
  const classes: Record<string, string> = {
    done: 'st-done',
    running: 'st-run',
    failed: 'st-fail',
    pending: 'st-pend',
  }
  return classes[status] ?? ''
}

/**
 * Format an ISO timestamp as "HH:MM" (24h UTC). Returns '' on invalid input.
 *
 * Uses UTC so display is stable across user timezones (matches backend ISO
 * timestamps 1:1). Local-TZ formatting belongs behind an explicit opt-in.
 */
export function formatTime(isoString: string | null | undefined): string {
  if (!isoString) {
    return ''
  }
  const d = new Date(isoString)
  if (isNaN(d.getTime())) {
    return ''
  }
  const h = d.getUTCHours().toString().padStart(2, '0')
  const m = d.getUTCMinutes().toString().padStart(2, '0')
  return `${h}:${m}`
}

/**
 * Capitalize first character and replace underscores with spaces.
 * Used to convert API enum values ("in_progress") to display labels ("In progress").
 *
 * SECURITY: Output is NOT HTML-safe. Always pass through `esc()` before
 * interpolating into innerHTML template literals or HTML attributes.
 */
export function capitalizeLabel(s: string | null | undefined): string {
  if (!s) {
    return ''
  }
  const spaced = s.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
